import type { TruckDraft } from '@numeo/types-core'
import { UserRole } from '@numeo/types-core'
import { Autocomplete, Button, FieldMessage } from 'components/ds2'
import { usePeople } from '@/hooks/team/usePeople'
import { useTeamMembers } from '@/hooks/team/useTeamMembers'
import { useContractors } from '../hooks/useContractors'
import { useAllTeamDrivers } from '../hooks/useAllTeamDrivers'
import { useDriverDirectoryActions } from '../hooks/useDriverDirectoryActions'
import { contractorDisplayName, eligibleBoxTruckOwners, truckOwnerId } from '../trucks/utils/contractorDisplayName'
import { getDispatcherDisplayName } from '../trucks/components/dispatcherName'
import type { PendingDriverSeat, PendingTruckAssignment } from 'store/trucksSlice'
import { choosePendingDriverSlot, PENDING_DRIVER_OPTION, pendingDriverSlots } from './pendingDriverSeat'
import { BoxTruckSectionHeading, boxTruckSectionClass } from './BoxTruckFormSection'

interface BoxTruckVehicleAssignmentsProps {
    truckData: TruckDraft
    ownerDriverIds?: string[]
    onUpdate: (field: string, value: string | number | null | string[]) => void
    pendingAssignment?: PendingTruckAssignment
    /** Where the driver being created now sits — required with `pendingAssignment.driverName`. */
    onPendingDriverSeatChange?: (seat: PendingDriverSeat) => void
    /** Whether the owner being created is still this vehicle's owner — required with `pendingAssignment.ownerName`. */
    onPendingOwnerChosenChange?: (chosen: boolean) => void
    /** Drawn without its card, under a divider: Quick Add Truck lays its sections out flat, as Quick Add Driver does. */
    bare?: boolean
}

/** The option value standing for the owner being created — never written into `boxTruck.ownerContractorId`. */
const PENDING_OWNER_OPTION = '__pending_owner__'

/**
 * Who works this vehicle: its drivers, dispatchers, and owner.
 *
 * "Owner" is Box Truck's word for the contractor that owns the vehicle — the Profiles page itself is called
 * Owners in Box Truck mode. Every picker here opens the same way, with a search field in the panel: a tenant's
 * roster runs to hundreds of drivers, which nobody can scroll to find one in.
 */
export const BoxTruckVehicleAssignments = ({
    truckData,
    ownerDriverIds,
    onUpdate,
    pendingAssignment,
    onPendingDriverSeatChange,
    onPendingOwnerChosenChange,
    bare = false,
}: BoxTruckVehicleAssignmentsProps) => {
    const { contractors } = useContractors()
    // The drivers this caller may work with (the directory, scoped by the server). Until it answers, the seated
    // drivers are still named; widening to every tenant driver on failure would offer assignments the server refuses.
    const roster = useAllTeamDrivers({ scoped: true })
    const { invalidateDrivers } = useDriverDirectoryActions()
    const seatedIds = (truckData.drivers ?? []).map(String)
    const { people: seatedPeople } = usePeople(seatedIds)
    const rosterReady = !roster.isLoading && !roster.isError
    const reachable: { id: string; name?: string; email?: string }[] = rosterReady
        ? roster.rows
        : seatedIds.flatMap((id) => {
              const person = seatedPeople.get(id)
              return person ? [{ id, name: person.name }] : []
          })

    const allowedDriverIds = new Set(ownerDriverIds ?? [])
    const drivers = reachable.filter((driver) => allowedDriverIds.size === 0 || allowedDriverIds.has(driver.id))
    const selected = seatedIds
    // Opened from Add Driver: the driver being created is an option like any other, Main by default, and can
    // be moved, replaced or cleared. They have no id, so their seat is held apart from `drivers`.
    const pendingDriverName = pendingAssignment?.driverName
    const pendingSeat: PendingDriverSeat = pendingDriverName ? (pendingAssignment?.driverSeat === undefined ? 'main' : pendingAssignment.driverSeat) : null
    // "Not assigned" stays a row of its own as well as the placeholder: a seat held by a driver outside this
    // caller's roster has no option to show, so it reads as empty with no ✕ — this row is how it is emptied.
    const driverOptions = [
        { value: '', label: 'Not assigned' },
        ...(pendingDriverName ? [{ value: PENDING_DRIVER_OPTION, label: `${pendingDriverName} (new driver)` }] : []),
        ...drivers.map((driver) => ({ value: driver.id, label: driver.name || driver.email || driver.id })),
    ]
    const slots = pendingDriverName ? pendingDriverSlots(selected, pendingSeat) : { main: selected[0] || '', second: selected[1] || '' }
    const setDriver = (index: number, driverId: string) => {
        if (pendingDriverName) {
            const next = choosePendingDriverSlot(selected, pendingSeat, index === 0 ? 'main' : 'second', driverId)
            onUpdate('drivers', next.drivers)
            onPendingDriverSeatChange?.(next.seat)
            return
        }
        const next = [...selected]
        if (driverId) next[index] = driverId
        else next.splice(index, 1)
        onUpdate('drivers', [...new Set(next.filter(Boolean))])
    }

    // The company's dispatchers (200, the most one page holds; the picker searches what it holds).
    const { items: dispatchers } = useTeamMembers({ roles: [UserRole.DISPATCHER], limit: 200 })
    const dispatcherOptions = dispatchers.map((dispatcher) => ({ value: dispatcher.id, label: getDispatcherDisplayName(dispatcher) }))
    const selectedDispatchers = (truckData.dispatchers ?? []).map(String)
    // Opened from a form that is creating an owner: like the new driver, that owner is an option chosen by
    // default, and any existing owner can be picked instead. It has no id, so the choice is held apart from
    // `boxTruck.ownerContractorId`, and the opener links the vehicle to it only while it is still chosen.
    const pendingOwnerName = pendingAssignment?.ownerName
    const hasPendingOwner = pendingOwnerName !== undefined
    const pendingOwnerChosen = hasPendingOwner && pendingAssignment?.ownerChosen !== false
    const ownerOptions = [
        ...(hasPendingOwner ? [{ value: PENDING_OWNER_OPTION, label: pendingOwnerName ? `${pendingOwnerName} (new owner)` : 'Owner being created' }] : []),
        ...eligibleBoxTruckOwners(contractors).map((contractor) => ({ value: String(contractor.id), label: contractorDisplayName(contractor) })),
    ]
    const ownerId = pendingOwnerChosen ? PENDING_OWNER_OPTION : truckOwnerId(truckData)
    const setOwner = (value: string | undefined) => {
        if (hasPendingOwner) onPendingOwnerChosenChange?.(value === PENDING_OWNER_OPTION)
        onUpdate('boxTruck.ownerContractorId', value && value !== PENDING_OWNER_OPTION ? value : null)
    }

    return (
        <section className={boxTruckSectionClass(bare)}>
            <BoxTruckSectionHeading bare={bare}>Assignments</BoxTruckSectionHeading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Autocomplete
                    variant="secondary"
                    label="Main Driver"
                    searchInPanel
                    placeholder="Not assigned"
                    emptyMessage="No drivers"
                    options={driverOptions}
                    value={slots.main ? [slots.main] : []}
                    onChange={(values) => setDriver(0, values[0] ?? '')}
                />
                <Autocomplete
                    variant="secondary"
                    label="Second Driver"
                    searchInPanel
                    placeholder="Not assigned"
                    emptyMessage="No drivers"
                    options={driverOptions}
                    value={slots.second ? [slots.second] : []}
                    onChange={(values) => setDriver(1, values[0] ?? '')}
                />
                {roster.isError ? (
                    <div className="md:col-span-2 -mt-2 flex items-center gap-3">
                        <FieldMessage variant="error">The driver list could not be loaded.</FieldMessage>
                        <Button variant="outline" size="sm" onClick={() => invalidateDrivers()}>
                            Try again
                        </Button>
                    </div>
                ) : null}
                <Autocomplete
                    variant="secondary"
                    label="Dispatchers"
                    multiple
                    placeholder="Select dispatchers"
                    emptyMessage="No dispatchers"
                    options={dispatcherOptions}
                    value={selectedDispatchers}
                    onChange={(values) => onUpdate('dispatchers', values)}
                />
                <div className="flex flex-col gap-1.5">
                    <Autocomplete
                        variant="secondary"
                        label="Owner"
                        searchInPanel
                        placeholder="Select owner"
                        emptyMessage="No owners yet"
                        options={ownerOptions}
                        value={ownerId ? [ownerId] : []}
                        onChange={(values) => setOwner(values[0])}
                    />
                    {pendingOwnerChosen ? <FieldMessage>Linked once the owner is saved</FieldMessage> : null}
                </div>
            </div>
        </section>
    )
}
